import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Upload, Search, X } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/use-auth";
import { listMediaFiltered } from "@/lib/media-manager";

const BUCKET = "product-media";

type Row = {
  name: string;
  full_path: string;
  is_folder: boolean;
  content_type: string | null;
};

interface Props {
  value?: string | null;
  onSelect: (url: string) => void;
  onClose: () => void;
}

export function InlineMediaPicker({ value, onSelect, onClose }: Props) {
  const qc = useQueryClient();
  const { profile } = useAuth();
  const isStaff = profile?.role === "staff" || profile?.role === "admin";
  const [search, setSearch] = useState("");
  const [uploading, setUploading] = useState(false);

  const { data: items = [], isLoading } = useQuery({
    enabled: !!profile && isStaff,
    queryKey: ["inline-media", search],
    queryFn: async () => {
      const rows = await listMediaFiltered({
        prefix: "",
        search: search.trim(),
        type: "image",
        size: "all",
        updated: "all",
        sortBy: "updated",
        sortDir: "desc",
        limit: 60,
      });
      return ((rows as Row[] | null) ?? [])
        .filter((r) => !r.is_folder)
        .map((r) => ({
          path: r.full_path,
          url: supabase.storage.from(BUCKET).getPublicUrl(r.full_path).data.publicUrl,
        }));
    },
  });

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const safe = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const path = `shop-builder/${Date.now()}-${safe}`;
      const { error } = await supabase.storage.from(BUCKET).upload(path, file, { upsert: false });
      if (error) throw error;
      const url = supabase.storage.from(BUCKET).getPublicUrl(path).data.publicUrl;
      await qc.invalidateQueries({ queryKey: ["inline-media"] });
      onSelect(url);
      toast.success("Uploaded");
    } catch (e) {
      toast.error((e as Error).message ?? "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-background/60 p-2 space-y-2">
      <div className="flex items-center justify-between text-[11px] font-medium">
        <span>Choose image</span>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search media…"
          className="h-8 pl-7 text-xs"
        />
      </div>
      <label className="flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-border bg-background py-2 text-[11px] cursor-pointer hover:border-primary/40">
        {uploading ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Upload className="h-3.5 w-3.5" />
        )}
        {uploading ? "Uploading…" : "Upload new"}
        <input
          type="file"
          accept="image/*"
          className="hidden"
          disabled={uploading}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleUpload(f);
            e.currentTarget.value = "";
          }}
        />
      </label>
      <div className="grid grid-cols-3 gap-1.5 max-h-60 overflow-y-auto">
        {isLoading && (
          <div className="col-span-3 flex justify-center py-4">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}
        {!isLoading && items.length === 0 && (
          <p className="col-span-3 text-center text-[11px] text-muted-foreground py-4">
            No images yet — upload one above.
          </p>
        )}
        {items.map((it) => (
          <button
            key={it.path}
            onClick={() => onSelect(it.url)}
            className={cn(
              "aspect-square rounded-md overflow-hidden border bg-muted hover:border-primary/60 transition-colors",
              value === it.url ? "border-primary ring-2 ring-primary/30" : "border-border",
            )}
            title={it.path}
          >
            <img src={it.url} alt="" className="h-full w-full object-cover" />
          </button>
        ))}
      </div>
    </div>
  );
}
